<template>
  <div style="min-height: calc(100vh - 158px);" class="card">
    <Loader v-if="loader" />
    <div style="background: #DBE7F2" class="p-2">
      <TitleButton :showBtn="false" :showAddNew="false" title="My Payments" />
      <div class="my-2 px-2">
          <DateQuerySetter @onClickGo="fetchData">
              <div class="col-12 col-sm-6 col-md-3 col-lg-2">
                  <label class="col-form-label">Status</label>
                  <v-select
                      v-model="filterStatus"
                      :options="statusOptions"
                      :reduce="option => option.value"
                      label="label"
                      :clearable="false"
                  />
              </div>
          </DateQuerySetter>
      </div>
    </div>
        
        <div class="col-12 px-2 mt-2 mb-4">
            <div class="card p-2" style="min-height: 200px">
                <ListTable 
                    :tableItems="tableItems" 
                    v-model:selectedSubscriptions="selectedSubscriptions" 
                    :showCheckbox="appliedStatus !== 'paid'"
                    :showActions="true"
                    :printingItemId="printingItemId"
                    :printingReceiptId="printingReceiptId"
                    @printInvoice="goToPrint"
                    @printReceipt="goToPrintReceipt"
                />

                <div v-if="tableItems.length && appliedStatus !== 'paid'" class="mt-2 text-center">
                    <button class="btn btn-success" @click="payOnline" :disabled="!selectedSubscriptions.length">
                        Pay Online
                    </button>
                </div>

                <div class="d-flex justify-content-end mt-3" v-if="tableItems.length > 0">
                    <Pagination
                        :offset="offset"
                        @onPageChange="onPageChange"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, inject } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import TitleButton from '@/components/atom/TitleButton.vue';
import Pagination from '@/components/atom/Pagination.vue';
import DateQuerySetter from '@/components/atom/DateQuerySetter.vue';
import Loader from '@/components/atom/loader/Loader.vue';
import ListTable from '@/components/molecule/subscription/ListTable.vue';

import handleOnlinePayment from '@/services/modules/onlinePayment.js';
import pdfPrinter from '@/services/utils/pdfPrinter';
import moneyReceiptHelper from '@/services/utils/pdf/moneyReceiptHelper';
import handleBarcode from '@/services/modules/barcode';
import handleCompany from '@/services/modules/company';
import useDate from '@/services/utils/day';

const router = useRouter();
const route = useRoute();
const store = useStore();
const showError = inject('showError');
const showSuccess = inject('showSuccess');

const { fetchSubscriptions, fetchSubscriptionInvoice, initiateSubscriptionPayment, fetchSubscriptionReceipt } = handleOnlinePayment();
const { generatePdf } = moneyReceiptHelper();
const { exportToPDF } = pdfPrinter();
const { fetchBarcode } = handleBarcode();
const { fetchCompanyInfo, fetchActiveCustomizationField } = handleCompany();
const { formatDate: formatDateDetailed } = useDate();

const companyId = computed(() => route.params.companyId);
const page = computed(() => route.query.page);
const offset = ref(20);

const filterStatus = ref('due');
const appliedStatus = ref('due');
const selectedSubscriptions = ref([]);

const customizationPdfFields = ref([]);
const printingItemId = ref(null);
const printingReceiptId = ref(null);

const statusOptions = ref([
    { label: 'Due', value: 'due' },
    { label: 'Partial', value: 'partial' },
    { label: 'Paid', value: 'paid' }
]);
const tableItems = ref([]);
const loader = ref(false);

const setPaginationData = (data) => {
    store.commit('setPaginationData', data);
};

const setPagination = (data) => {
    setPaginationData({
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    });
};

const fetchData = async () => {
    try {
        loader.value = true;
        selectedSubscriptions.value = [];
        appliedStatus.value = filterStatus.value;
        let query = `?company_id=${companyId.value}&offset=${offset.value}&status=${filterStatus.value}`;
        
        if (route.query.start && route.query.end) {
            query += `&start_date=${route.query.start}&end_date=${route.query.end}`;
        }
        
        if (page.value) {
            query += `&page=${page.value}`;
        }

        const res = await fetchSubscriptions(query);
        
        if (!res.status) {
            tableItems.value = [];
            store.commit("resetPagination");
            return;
        }
        
        tableItems.value = res.data.data;
        
        if (res.data.data.length > 0) {
            setPagination(res.data);
        }
    } catch (error) {
        showError('Failed to fetch subscriptions.');
        tableItems.value = [];
        store.commit("resetPagination");
    } finally {
        loader.value = false;
    }
};

const onPageChange = async (pageNo) => {
    let routeQuery = Object.assign({}, route.query);
    routeQuery.page = pageNo;
    await router.push({ path: route.path, query: routeQuery });
    fetchData();
};

const goToPrint = async (item) => {
    if (printingItemId.value === item.id) return;
    printingItemId.value = item.id;
    try {
        const res = await fetchSubscriptionInvoice(item.id, `?company_id=${companyId.value}`);
        if (!res.status || !res.data) {
            showError(res.message || 'Failed to fetch invoice');
            return;
        }
        const invoice = res.data;
        
        const companyRes = await fetchCompanyInfo(companyId.value);
        const companyInfo = companyRes.data || {};
        
        const userState = store.state.user;
        const username = (userState && userState.user) ? userState.user.name : '';
        
        let qrcodeData = JSON.stringify({
            ref_no: invoice.bill_number,
            ref_date: invoice.date,
            created_by: username,
            created_date_time: formatDateDetailed(new Date())
        });
        
        const barcodeQuery = `?code=${invoice.bill_number}&qrcode=${qrcodeData}`;
        const barcodeRes = await fetchBarcode(barcodeQuery);
        
        const barcode = barcodeRes.barcode;
        const qrcode = barcodeRes.qrcode;
        
        exportToPDF(companyInfo, invoice, barcode, qrcode, username, 1, [], false, customizationPdfFields.value);
    } catch (error) {
        console.error(error);
        showError('Failed to print invoice');
    } finally {
        printingItemId.value = null;
    }
};

const goToPrintReceipt = async (item) => {
    if (printingReceiptId.value === item.id) return;
    printingReceiptId.value = item.id;
    try {
        const companyRes = await fetchCompanyInfo(companyId.value);
        let companyInfo = {};
        if (companyRes.status) {
            companyInfo = companyRes.data;
        }

        const receiptRes = await fetchSubscriptionReceipt(item.id, `?company_id=${companyId.value}`);
        if (!receiptRes || !receiptRes.status || !receiptRes.data) {
            showError(receiptRes.message || 'Failed to fetch receipt');
            return;
        }
        
        generatePdf(companyInfo, receiptRes.data);
    } catch (error) {
        console.error(error);
        showError('Failed to print receipt');
    } finally {
        printingReceiptId.value = null;
    }
};

const payOnline = async () => {
    if (selectedSubscriptions.value.length === 0) {
        showError('Please select a subscription to pay');
        return;
    }

    
    const urlObj = new URL(window.location.href);
    urlObj.searchParams.delete('payment_status');
    urlObj.searchParams.delete('message');
    urlObj.searchParams.delete('receipt_id');
    urlObj.searchParams.delete('tran_id');

    const query = { ...route.query };
    delete query.payment_status;
    delete query.message;
    delete query.receipt_id;
    delete query.tran_id;
    query.return_to = 'my-payments';

    const successRoute = router.resolve({
      name: 'payment-status',
      params: {
        companyId: route.params.companyId,
        moduleId: route.params.moduleId,
        menuId: route.params.menuId,
        pageId: route.params.pageId
      },
      query: query
    });

    const successUrl = window.location.origin + successRoute.href;

    try {
        const res = await initiateSubscriptionPayment({
            subscription_ids: selectedSubscriptions.value,
            company_id: companyId.value,
            return_url: successUrl,
            success_url: successUrl
        });

        if (!res.status || !res.gateway_url) {
            showError(res.message || 'Failed to initiate payment.');
            return;
        }

        window.location.href = res.gateway_url;
    } catch (error) {
        showError('Something went wrong during payment initiation.');
    }
};

const getActiveFiledCustomization = async () => {
  const companyQuery = `?company_id=${companyId.value}&customization_type=pdf&type=sale_invoice_pdf_list`;
  await fetchActiveCustomizationField(companyQuery).then((res) => {
    if(!res.status) return;
    customizationPdfFields.value = res.data;
  });
}

onMounted(() => {
    if (route.query.payment_status === 'success') {
        showSuccess('Payment processed successfully!');
        router.replace({ query: { ...route.query, payment_status: undefined, receipt_id: undefined, tran_id: undefined, message: undefined } });
    } 
    
    if (route.query.payment_status === 'failed' || route.query.payment_status === 'error') {
        showError(route.query.message || 'Payment failed or cancelled.');
        router.replace({ query: { ...route.query, payment_status: undefined, receipt_id: undefined, tran_id: undefined, message: undefined } });
    }

    fetchData();
    getActiveFiledCustomization();
});
</script>
